export const privacyPolicy = {
  slug: "privacy-policy",
  kicker: "LEGAL / PRIVACY",
  title: "Privacy Policy",
  intro: "Your trust matters as much as the parts we source. This policy explains what information DECODEAUTODEALS collects, why we collect it, and the choices you have.",
  sections: [
    {
      heading: "Information we collect",
      paragraphs: ["When you request a quotation or contact us, we collect the details you share with us directly."],
      list: [
        "Contact details — your name, email address, and phone number.",
        "Vehicle details — year, make, model, and the part you are looking for.",
        "Delivery details — your ZIP code and, when an order is confirmed, your shipping address.",
        "Usage data — basic technical information about how you browse our website.",
      ],
    },
    {
      heading: "How we use your information",
      paragraphs: ["We use the information you provide to respond to your quote requests, verify part fitment and availability, arrange nationwide delivery, and keep you updated about your order. We may also use your contact details to follow up on a request you started but did not complete."],
    },
    {
      heading: "How we share information",
      paragraphs: [
        "We do not sell, rent, or trade your personal information to anyone.",
        "We share information only with the partners needed to serve you — for example, our own shipping network and fulfillment hubs to deliver your part, and service providers who help us operate the website. If online payments are introduced, your payment details will be processed by a secure payment provider and will not be stored on our servers.",
      ],
    },
    {
      heading: "Data retention and security",
      paragraphs: ["Quote requests are kept so our parts desk can reference your history and serve you faster on your next visit. We apply reasonable administrative and technical safeguards to protect your information. No method of transmission over the internet is completely secure, but we work to keep your data protected at every step."],
    },
    {
      heading: "Your choices",
      paragraphs: ["You may ask us to access, correct, or delete the personal information we hold about you at any time. Simply reach out through the quote form or the contact details on our website and we will respond promptly."],
    },
    {
      heading: "Cookies",
      paragraphs: ["Our website may use basic cookies and similar technologies to keep the site running smoothly and to understand which pages visitors find most useful. You can disable cookies in your browser settings, though some features may work differently."],
    },
    {
      heading: "Changes to this policy",
      paragraphs: ["We may update this policy from time to time. The date at the bottom of this page shows the most recent revision. Continued use of the website after an update means you accept the revised policy."],
    },
  ],
};

export const returnsRefunds = {
  slug: "returns-refunds",
  kicker: "LEGAL / RETURNS & REFUNDS",
  title: "Return & Refund Policy",
  intro: "Every part we ship is confirmed with you before payment. If something is not right when it arrives, this policy explains how returns and refunds work.",
  sections: [
    {
      heading: "Our quote-first promise",
      paragraphs: ["Because every order begins with a quotation, our parts desk confirms fitment, condition, mileage, and price with you before any payment is taken. This means fewer surprises — and on the rare occasion something goes wrong, a clear path to make it right."],
    },
    {
      heading: "Return window",
      paragraphs: ["You may request a return within 30 days of the delivery date. Requests made after 30 days cannot be accepted unless covered under our Warranty Policy."],
    },
    {
      heading: "Condition requirements",
      paragraphs: ["To be eligible for a refund, a part must be returned in the same condition in which it was delivered:"],
      list: [
        "Uninstalled and unaltered, with all original tags and markings intact.",
        "Complete with any components, sensors, or accessories described in your quote.",
        "Returned in suitable packaging that protects the part in transit.",
      ],
    },
    {
      heading: "Items that cannot be returned",
      list: [
        "Electrical and electronic components once installed or connected.",
        "Special-order parts sourced specifically at your request, unless faulty.",
        "Parts clearly described and sold in as-is condition.",
      ],
    },
    {
      heading: "Damaged or incorrect shipments",
      paragraphs: ["If your part arrives damaged, or is not the part confirmed in your quote, contact us within 48 hours of delivery with your quote reference number and photographs of the part and packaging. We will arrange collection through our own shipping network and provide a replacement or full refund — including shipping costs."],
    },
    {
      heading: "Core charges",
      paragraphs: ["Some parts, such as engines and transmissions, may carry a refundable core charge. The core charge is refunded in full once your old unit is returned to us in rebuildable condition."],
    },
    {
      heading: "How refunds are processed",
      paragraphs: ["Once a returned part reaches our warehouse, it is inspected within 3 business days. Approved refunds are issued to the original payment method within 5–10 business days of inspection. Return shipping for change-of-mind returns is the customer's responsibility."],
    },
    {
      heading: "How to start a return",
      paragraphs: ["Send us your quote reference number (it begins with DQ-) through the quote form on our website, along with the reason for the return. Our parts desk will confirm eligibility and send return instructions."],
    },
  ],
};

export const termsConditions = {
  slug: "terms-conditions",
  kicker: "LEGAL / TERMS & CONDITIONS",
  title: "Terms & Conditions",
  intro: "These terms govern your use of the DECODEAUTODEALS website and any purchase of used auto parts from us. By using this website or requesting a quote, you agree to these terms.",
  sections: [
    {
      heading: "About DECODEAUTODEALS",
      paragraphs: ["DECODEAUTODEALS is an independent seller of quality used auto parts based in the United States, operating fulfillment hubs across the country and delivering nationwide through its own shipping network."],
    },
    {
      heading: "Quotations and orders",
      paragraphs: [
        "Quotes provided through this website are estimates based on the information you share with us. An order is only final once availability, fitment, condition, and total price — including shipping — have been confirmed with you in writing.",
        "Prices shown as starting from on this website are indicative only and vary with availability, mileage, and condition.",
      ],
    },
    {
      heading: "Part condition and fitment",
      paragraphs: [
        "Unless stated otherwise, all parts are used and may show normal signs of wear consistent with their age and mileage. Significant defects are always disclosed in your quote confirmation.",
        "Accurate fitment depends on the accuracy of the vehicle information you provide. We recommend including your VIN with every request. We are not responsible for fitment issues arising from incomplete or inaccurate vehicle details.",
      ],
    },
    {
      heading: "Pricing and payment",
      paragraphs: ["Confirmed prices are valid for the period stated in your quote. Online payment options may be introduced on this website; until then, payment arrangements are confirmed directly with our parts desk. Title to a part passes to you upon confirmed delivery."],
    },
    {
      heading: "Shipping and delivery",
      paragraphs: ["We ship to all 50 states through our own shipping network. Delivery timeframes are estimates and may vary with distance, weather, and carrier conditions. Risk of loss passes to you upon delivery to the address provided."],
    },
    {
      heading: "Acceptable use",
      paragraphs: ["You agree not to misuse this website, including submitting false requests, attempting to interfere with its operation, or scraping its content for commercial purposes."],
    },
    {
      heading: "Intellectual property",
      paragraphs: ["All content on this website — including text, design, imagery, and the DECODEAUTODEALS name and branding — belongs to DECODEAUTODEALS and may not be reproduced without written permission."],
    },
    {
      heading: "Limitation of liability",
      paragraphs: ["To the fullest extent permitted by law, DECODEAUTODEALS is not liable for indirect or consequential damages, including labor costs, towing, loss of use, or lost income arising from the purchase or installation of a part. Our total liability for any claim is limited to the purchase price of the part in question."],
    },
    {
      heading: "Governing law",
      paragraphs: ["These terms are governed by the laws of the United States and the state in which DECODEAUTODEALS is principally operated, without regard to conflict-of-law rules."],
    },
    {
      heading: "Changes to these terms",
      paragraphs: ["We may revise these terms from time to time. The version published on this page at the time of your order applies to that order."],
    },
  ],
};

export const warrantyPolicy = {
  slug: "warranty-policy",
  kicker: "LEGAL / WARRANTY",
  title: "Warranty Policy",
  intro: "We stand behind every part we ship. This policy explains the warranty coverage that comes with your purchase and how to make a claim.",
  sections: [
    {
      heading: "Standard coverage",
      paragraphs: ["Unless a different term is stated in your written quote confirmation, the following coverage applies from the date of delivery:"],
      list: [
        "Engines and transmissions — 90-day replacement warranty.",
        "Turbos, differentials, and transfer cases — 60-day replacement warranty.",
        "All other mechanical parts — 30-day replacement warranty.",
      ],
    },
    {
      heading: "What is covered",
      paragraphs: ["The warranty covers failure of the part itself under normal use — for example, internal mechanical failure of an engine or transmission that was correctly installed and maintained. Where a replacement is available, we replace the part; where it is not, we refund the purchase price."],
    },
    {
      heading: "What is not covered",
      list: [
        "Labor costs, fluids, gaskets, seals, filters, belts, and other installation consumables.",
        "Damage caused by improper installation, overheating, low fluid levels, misuse, racing, or off-road use.",
        "Damage caused by a related failed component not supplied by us.",
        "Parts installed in vehicles other than the vehicle confirmed in your quote.",
        "Normal wear consistent with a used part's age and mileage.",
      ],
    },
    {
      heading: "Keeping your warranty valid",
      paragraphs: ["To keep coverage in place, parts must be installed by a licensed repair facility, all required fluids and seals must be replaced at installation, and heat tabs and identification markings applied by our warehouses must remain intact. Please keep your invoice and installation records."],
    },
    {
      heading: "How to make a claim",
      paragraphs: ["Contact our parts desk through the quote form with your quote reference number, a description of the issue, and photographs or a diagnostic report where available. Do not disassemble the part before contacting us, as disassembly may void coverage. Approved claims are resolved by replacement or refund, at our discretion."],
    },
    {
      heading: "Shipping on warranty claims",
      paragraphs: ["For approved warranty claims within the coverage period, we arrange collection and redelivery through our own shipping network at no cost to you."],
    },
  ],
};
